import { MarketingResource } from "creso-core/types";
import { Calendar, Clock } from "lucide-react";
import {
  Button,
  cn,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "web-design-system";
import { useRef, useState } from "react";
import {
  getResourceLink,
  getVideoPreviewImage,
  getYoutubeEmbedUrl,
  formatWebinarDateRange,
  formatWebinarEventTime,
  isWebinarEventPast,
} from "creso-core/utils";
import { VideoThumbnail } from "./video-thumbnail";
import VideoDialog from "./video-dialog";

function WebinarTitle({ title }: { title: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleEl = (
    <div ref={ref} className="text-lg font-medium line-clamp-2">
      {title}
    </div>
  );

  if (title.length < 60) {
    return titleEl;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{titleEl}</TooltipTrigger>
      <TooltipContent portal className="max-w-52">
        {title}
      </TooltipContent>
    </Tooltip>
  );
}

export function WebinarFeaturedCard({
  item,
  isUpcoming = true,
  className,
}: {
  item: MarketingResource;
  isUpcoming?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const link = getResourceLink(item);
  const embedUrl = getYoutubeEmbedUrl(link);
  const thumbnail = getVideoPreviewImage(item);
  const isRegistrationClosed = isWebinarEventPast(item);

  return (
    <>
      <div
        className={cn(
          "flex flex-col sm:flex-row rounded-lg border border-border/50 bg-background overflow-hidden h-76 p-2 gap-4 w-full",
          className
        )}
        onClick={() => {
          if (!isUpcoming) {
            setOpen(true);
          }
        }}
      >
        <VideoThumbnail
          title={item.title}
          src={thumbnail ?? ""}
          className={cn(
            "w-1/2 h-full p-0 min-h-56",
            !isUpcoming && "cursor-pointer"
          )}
          showPlayIcon={!isUpcoming}
          duration={!isUpcoming && item.duration ? item.duration : undefined}
        />
        <div className="flex flex-col gap-6 pt-2 flex-1 min-w-0">
          {isUpcoming ? (
            <>
              <div className="flex flex-col gap-2">
                <WebinarTitle title={item.title} />
                {item.metadata?.speaker &&
                typeof item.metadata.speaker === "string" ? (
                  <div className="text-sm text-muted-foreground">
                    by {item.metadata?.speaker}
                  </div>
                ) : null}
              </div>
              <ul className="flex flex-col gap-4 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Calendar className="size-4 shrink-0 text-muted-foreground" />
                  {formatWebinarDateRange(item)}
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="size-4 shrink-0 text-muted-foreground" />
                  {formatWebinarEventTime(item)}
                </li>
              </ul>
              <Button
                className="w-full mt-auto mb-2"
                disabled={isRegistrationClosed}
                onClick={() => {
                  if (isRegistrationClosed) {
                    return;
                  }

                  window.open(getResourceLink(item), "_blank");
                }}
              >
                Register
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <WebinarTitle title={item.title} />
              <div className="text-sm text-muted-foreground">
                {formatWebinarDateRange(item)}
              </div>
              {item.metadata?.speaker &&
              typeof item.metadata.speaker === "string" ? (
                <div className="text-sm text-muted-foreground mt-4">
                  by {item.metadata?.speaker}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <VideoDialog
        open={open}
        setOpen={setOpen}
        embedUrl={embedUrl ?? ""}
        title={item.title}
      />
    </>
  );
}
